import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { useProduct, useRefreshProduct, useDeleteProduct } from '../hooks/useProducts';
import { useNavigate } from 'react-router-dom';
import SimilarProducts from '../components/SimilarProducts';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  crawling: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

function formatPrice(value?: number) {
  if (value == null) return '-';
  return `$${value.toFixed(2)}`;
}

function timeAgo(dateStr?: string) {
  if (!dateStr) return null;
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: product, isLoading, isError } = useProduct(id!);
  const refreshProduct = useRefreshProduct();
  const deleteProduct = useDeleteProduct();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="space-y-4">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="rounded-lg border border-border bg-card p-6">
          <p className="text-sm text-muted-foreground">Product not found.</p>
        </div>
      </div>
    );
  }

  const buyUrl = product.redirect_url || product.current_best_url || product.normalized_url;

  return (
    <div className="space-y-6">
      <Link to={product.wishlist_id ? `/wishlists/${product.wishlist_id}` : '/dashboard'} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {/* Hero: image + basic info */}
        <div className="flex flex-col sm:flex-row">
          <div className="sm:w-64 h-64 flex-shrink-0 bg-muted">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="h-full w-full object-contain"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground text-sm">
                No image
              </div>
            )}
          </div>

          <div className="flex-1 p-6 space-y-4">
            <div>
              <h1 className="text-xl font-semibold text-card-foreground">{product.name}</h1>
              {product.retailer_name && (
                <p className="text-sm text-muted-foreground mt-1">{product.retailer_name}</p>
              )}
              {(product.category_l1 || product.category_l2 || product.category_l3) && (
                <p className="text-xs text-muted-foreground mt-1">
                  {[product.category_l1, product.category_l2, product.category_l3]
                    .filter(Boolean)
                    .join(' › ')}
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Current Price</p>
                <p className="text-2xl font-bold text-card-foreground">{formatPrice(product.current_best_price)}</p>
              </div>
              {product.target_price != null && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Target</p>
                  <p className="text-2xl font-bold text-green-600">{formatPrice(product.target_price)}</p>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[product.enrichment_status] ?? ''}`}>
                {product.enrichment_status}
              </span>
              {product.last_checked_at && (
                <span className="text-muted-foreground" title={new Date(product.last_checked_at).toLocaleString()}>
                  Checked {timeAgo(product.last_checked_at)}
                </span>
              )}
              {product.redirect_url && (
                <span className="inline-flex rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-medium">
                  Affiliate link
                  {product.affiliate_commission_rate != null && ` (${(product.affiliate_commission_rate * 100).toFixed(1)}%)`}
                </span>
              )}
            </div>

            {/* Buy button — mirrors mobile _launchProductUrl flow */}
            {buyUrl && (
              <a
                href={buyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                {product.redirect_url
                  ? `Buy (Affiliate${product.affiliate_commission_rate != null ? ` — ${(product.affiliate_commission_rate * 100).toFixed(1)}% commission` : ''})`
                  : 'Buy'}
              </a>
            )}

            {/* Debug: show which URL is being used */}
            {buyUrl && (
              <p className="text-xs text-muted-foreground break-all">
                {product.redirect_url ? 'Affiliate: ' : 'Direct: '}{buyUrl}
              </p>
            )}
          </div>
        </div>

        {/* Description */}
        {product.description && (
          <div className="border-t border-border px-6 py-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Description</p>
            <p className="text-sm text-card-foreground whitespace-pre-line">{product.description}</p>
          </div>
        )}

        {/* Actions bar */}
        <div className="border-t border-border px-6 py-3 flex items-center gap-2">
          <button
            onClick={() => refreshProduct.mutate(product.id)}
            disabled={product.enrichment_status === 'pending'}
            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-primary/10 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh Price
          </button>
          <button
            onClick={async () => {
              if (window.confirm(`Delete "${product.name}"?`)) {
                await deleteProduct.mutateAsync(product.id);
                navigate(product.wishlist_id ? `/wishlists/${product.wishlist_id}` : '/dashboard');
              }
            }}
            className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Similar Products */}
      {product.id && (
        <div className="rounded-lg border border-border bg-card p-6">
          <SimilarProducts productId={product.id} />
        </div>
      )}
    </div>
  );
}
